import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { KnobModule } from 'primeng/knob';
import { ChartModule } from 'primeng/chart';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnalysisStore } from '../../../core/shared/t0-model/analysis.store';
import { LocationSelectionService } from '../../../pages/service/location-selection.service';
import { T0SimulationService, T0SimulationParameters } from '../../../core/shared/t0-model/t0-simulation.service';
import { T0ApiResponse, Year } from '../../../core/shared/t0-model/t0-response-model';
import { YearStore } from '../../../core/shared/t0-model/year.store';

type UiSim = {
  name: string;
  createdAt: number;
  params: T0SimulationParameters;
};

type BaselineParams = Record<string, number>;

@Component({
  selector: 'app-t0-simulation-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, KnobModule, ChartModule, DialogModule, InputTextModule, TooltipModule, DecimalPipe],
  templateUrl: './t0-simulation-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class T0SimulationPanelComponent implements OnInit, OnDestroy {
  private store = inject(AnalysisStore);
  private simApi = inject(T0SimulationService);
  private locationSel = inject(LocationSelectionService);
  private yearStore = inject(YearStore);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();

  preventionKnob = 0;
  screeningKnob = 0;
  treatmentKnob = 0;

  preventionBase = 0.03;
  screeningBase = 0.11;
  treatmentBase = 0.27;

  preventionValue = this.preventionBase;
  screeningValue = this.screeningBase;
  treatmentValue = this.treatmentBase;

  coverageData: any;
  coverageOptions: any;

  centerTotalLabel = '-';
  normalLabel = '-';
  prehtLabel = '-';
  htLabel = '-';
  cvdLabel = '-';

  sims: UiSim[] = [];
  selectedNames = new Set<string>();
  selectAll = false;

  runDialogVisible = false;
  renameDialogVisible = false;
  viewDialogVisible = false;

  deleteDialogVisible = false;
  deletePendingNames: string[] = [];

  runName = '';
  renameOld = '';
  renameNew = '';
  viewSim: UiSim | null = null;

  busy = false;
  errorMsg = '';

  private lastBaselineSig = '';

  ngOnInit(): void {
    this.coverageOptions = {
      maintainAspectRatio: true,
      cutout: '75%',
      plugins: { legend: { display: false } }
    };

    this.coverageData = {
      labels: ['Normal BP', 'Prehypertension', 'Hypertension', 'CVD'],
      datasets: [
        {
          data: [0, 0, 0, 0],
          backgroundColor: ['#aa1646', '#e95887', '#fec2d6', '#d41c58'],
          borderColor: '#ffffff',
          borderWidth: 2
        }
      ]
    };

    this.recomputeRatesFromKnobs();
    this.syncSimsFromStore();

    combineLatest([this.store.runsList$, this.store.selectedRunId$, this.yearStore.year$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([runs, selId, selectedYear]) => {
        if (!runs?.length) {
          this.resetCoverageUi();
          this.syncSimsFromStore();
          this.cdr.markForCheck();
          return;
        }

        const run = selId ? runs.find((r) => r.id === selId) : runs[runs.length - 1];
        const t0 = run?.t0;
        if (!t0) {
          this.resetCoverageUi();
          this.syncSimsFromStore();
          this.cdr.markForCheck();
          return;
        }

        this.applyBaselineParamsIfChanged(t0);

        const year = this.store.pickNearestOrLatestYear(t0, Number(selectedYear)) as Year | null;
        if (year === null) {
          this.resetCoverageUi();
          this.syncSimsFromStore();
          this.cdr.markForCheck();
          return;
        }

        const normal = this.store.pick(t0, 'NormalBP_Male', year) + this.store.pick(t0, 'NormalBP_Female', year);
        const preht = this.store.pick(t0, 'Prehypertension', year);
        const ht = this.store.pick(t0, 'Hypertension_Male', year) + this.store.pick(t0, 'Hypertension_Female', year);
        const cvd = this.store.pick(t0, 'CVD_Patients', year);
        const total = normal + preht + ht + cvd;

        this.centerTotalLabel = this.fmtCompact(total);
        this.normalLabel = this.fmt(normal);
        this.prehtLabel = this.fmt(preht);
        this.htLabel = this.fmt(ht);
        this.cvdLabel = this.fmt(cvd);

        this.coverageData = {
          labels: ['Normal BP', 'Prehypertension', 'Hypertension', 'CVD'],
          datasets: [
            {
              data: [normal, preht, ht, cvd],
              backgroundColor: ['#aa1646', '#e95887', '#fec2d6', '#d41c58'],
              borderColor: '#ffffff',
              borderWidth: 2
            }
          ]
        };

        this.syncSimsFromStore();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPreventionKnobChange(): void {
    this.recomputeRatesFromKnobs();
  }

  onScreeningKnobChange(): void {
    this.recomputeRatesFromKnobs();
  }

  onTreatmentKnobChange(): void {
    this.recomputeRatesFromKnobs();
  }

  private recomputeRatesFromKnobs(): void {
    const p = this.clamp(Number(this.preventionKnob), 0, 100);
    const prevention = this.preventionBase * (1 - p / 100);

    const s = this.clamp(Number(this.screeningKnob), 0, 300);
    const screening = this.screeningBase * (1 + s / 100);

    const t = this.clamp(Number(this.treatmentKnob), 0, 300);
    const treatment = this.treatmentBase * (1 + t / 100);

    this.preventionValue = Number.isFinite(prevention) ? prevention : 0;
    this.screeningValue = Number.isFinite(screening) ? screening : 0;
    this.treatmentValue = Number.isFinite(treatment) ? treatment : 0;

    this.cdr.markForCheck();
  }

  private clamp(n: number, min: number, max: number): number {
    if (!Number.isFinite(n)) return min;
    return Math.max(min, Math.min(max, n));
  }

  private resetCoverageUi(): void {
    this.centerTotalLabel = '-';
    this.normalLabel = '-';
    this.prehtLabel = '-';
    this.htLabel = '-';
    this.cvdLabel = '-';

    this.coverageData = {
      labels: ['Normal BP', 'Prehypertension', 'Hypertension', 'CVD'],
      datasets: [
        {
          data: [0, 0, 0, 0],
          backgroundColor: ['#aa1646', '#e95887', '#fec2d6', '#d41c58'],
          borderColor: '#ffffff',
          borderWidth: 2
        }
      ]
    };
  }

  private applyBaselineParamsIfChanged(t0: T0ApiResponse): void {
    const bp = (t0 as any)?.baseline_parameters as BaselineParams | undefined;
    if (!bp) return;

    const sig = JSON.stringify({
      d: bp['learning diagnosis rate'],
      p: bp['learning prehypertension onset rate'],
      t: bp['learning treatment uptake rate']
    });

    if (sig === this.lastBaselineSig) return;
    this.lastBaselineSig = sig;

    const toNum = (n: any) => {
      const v = Number(n);
      return Number.isFinite(v) ? v : null;
    };

    const preventionBase = toNum(bp['learning prehypertension onset rate']);
    const screeningBase = toNum(bp['learning diagnosis rate']);
    const treatmentBase = toNum(bp['learning treatment uptake rate']);

    if (preventionBase !== null) this.preventionBase = preventionBase;
    if (screeningBase !== null) this.screeningBase = screeningBase;
    if (treatmentBase !== null) this.treatmentBase = treatmentBase;

    this.preventionKnob = 0;
    this.screeningKnob = 0;
    this.treatmentKnob = 0;

    this.recomputeRatesFromKnobs();
  }

  openRunDialog(): void {
    this.errorMsg = '';
    this.runName = this.nextDefaultName();
    this.runDialogVisible = true;
    this.cdr.markForCheck();
  }

  async confirmRun(): Promise<void> {
    const name = this.normalizeName(this.runName);
    if (!name) return;

    if (this.hasName(name)) {
      this.errorMsg = 'Simulation name already exists.';
      this.cdr.markForCheck();
      return;
    }

    const params = this.buildParams();

    if (this.isUnchangedFromBaseline(params)) {
      this.errorMsg = 'Please modify at least one parameter before running a new simulation.';
      this.cdr.markForCheck();
      return;
    }

    if (this.hasDuplicateParams(params)) {
      this.errorMsg = 'A simulation with the same parameter values already exists.';
      this.cdr.markForCheck();
      return;
    }

    this.runDialogVisible = false;
    await this.runSimulation(name);
  }

  private async runSimulation(name: string): Promise<void> {
    this.errorMsg = '';
    this.busy = true;
    this.cdr.markForCheck();

    try {
      const session_id = sessionStorage.getItem('session_id') || '';
      const city = this.locationSel.getSelected()?.city || '';
      if (!session_id) throw new Error('Missing session_id in sessionStorage.');
      if (!city) throw new Error('Missing selected city.');

      const params = this.buildParams();

      const res = await this.simApi.createSimulation({
        session_id,
        city,
        simulation_name: name,
        parameters: params
      });

      const anyRes: any = res;
      const ok = anyRes && Array.isArray(anyRes.years);
      if (!ok) throw new Error(anyRes?.message || 'Simulation request failed.');

      anyRes.__ui_params = { ...params };

      const t0 = anyRes as T0ApiResponse;
      this.store.upsertRun(t0, name);

      this.syncSimsFromStore();
      this.recomputeSelectAll();
    } catch (e: any) {
      this.errorMsg = e?.message || 'Something went wrong.';
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  openView(sim: UiSim): void {
    this.viewSim = sim;
    this.viewDialogVisible = true;
    this.cdr.markForCheck();
  }

  openRename(sim: UiSim): void {
    this.errorMsg = '';
    this.renameOld = sim.name;
    this.renameNew = sim.name;
    this.renameDialogVisible = true;
    this.cdr.markForCheck();
  }

  async confirmRename(): Promise<void> {
    const oldName = this.normalizeName(this.renameOld);
    const newName = this.normalizeName(this.renameNew);
    if (!oldName || !newName) return;

    if (this.isBaselineName(oldName)) {
      this.errorMsg = 'Baseline cannot be renamed.';
      this.cdr.markForCheck();
      return;
    }

    if (oldName.toLowerCase() !== newName.toLowerCase() && this.hasName(newName)) {
      this.errorMsg = 'Simulation name already exists.';
      this.cdr.markForCheck();
      return;
    }

    this.busy = true;
    this.cdr.markForCheck();

    try {
      const session_id = sessionStorage.getItem('session_id') || '';
      const city = this.locationSel.getSelected()?.city || '';
      if (!session_id) throw new Error('Missing session_id in sessionStorage.');
      if (!city) throw new Error('Missing selected city.');

      const res: any = await this.simApi.renameSimulation({
        session_id,
        city,
        old_simulation_name: oldName,
        new_simulation_name: newName
      });

      if (!this.isOkStatus(res, ['UPDATED', 'SUCCESS'])) {
        throw new Error(res?.message || 'Rename failed.');
      }

      this.store.renameRunTitle(oldName, newName);
      this.syncSimsFromStore();

      if (this.selectedNames.has(oldName)) {
        this.selectedNames.delete(oldName);
        this.selectedNames.add(newName);
      }

      this.renameDialogVisible = false;
      this.recomputeSelectAll();
    } catch (e: any) {
      this.errorMsg = e?.message || 'Something went wrong.';
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  requestDeleteOne(sim: UiSim): void {
    this.errorMsg = '';
    if (this.isBaselineName(sim.name)) {
      this.errorMsg = 'Baseline cannot be deleted.';
      this.cdr.markForCheck();
      return;
    }
    this.deletePendingNames = [sim.name];
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  requestDeleteSelected(): void {
    this.errorMsg = '';
    const names = Array.from(this.selectedNames);
    if (!names.length) return;

    if (names.some((n) => this.isBaselineName(n))) {
      this.errorMsg = 'Baseline cannot be deleted.';
      this.cdr.markForCheck();
      return;
    }

    this.deletePendingNames = names;
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  cancelDelete(): void {
    this.deleteDialogVisible = false;
    this.deletePendingNames = [];
    this.cdr.markForCheck();
  }

  async confirmDelete(): Promise<void> {
    const names = [...this.deletePendingNames];
    this.deleteDialogVisible = false;
    this.deletePendingNames = [];
    await this.deleteByNames(names);
  }

  private async deleteByNames(names: string[]): Promise<void> {
    const normNames = names.map((n) => this.normalizeName(n)).filter(Boolean);
    if (!normNames.length) return;

    if (normNames.some((n) => this.isBaselineName(n))) {
      this.errorMsg = 'Baseline cannot be deleted.';
      this.cdr.markForCheck();
      return;
    }

    this.errorMsg = '';
    this.busy = true;
    this.cdr.markForCheck();

    try {
      const session_id = sessionStorage.getItem('session_id') || '';
      const city = this.locationSel.getSelected()?.city || '';
      if (!session_id) throw new Error('Missing session_id in sessionStorage.');
      if (!city) throw new Error('Missing selected city.');

      for (const n of normNames) {
        const res: any = await this.simApi.deleteSimulation({
          session_id,
          city,
          old_simulation_name: n
        });

        if (!this.isOkStatus(res, ['DELETED', 'SUCCESS'])) {
          throw new Error(res?.message || `Delete failed for ${n}`);
        }
      }

      this.store.deleteRunsByTitle(normNames);
      this.syncSimsFromStore();

      normNames.forEach((n) => this.selectedNames.delete(n));
      this.recomputeSelectAll();
    } catch (e: any) {
      this.errorMsg = e?.message || 'Something went wrong.';
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  toggleSelectAll(): void {
    this.selectAll = !this.selectAll;
    this.selectedNames.clear();
    if (this.selectAll) this.sims.forEach((s) => this.selectedNames.add(s.name));
    this.cdr.markForCheck();
  }

  toggleRow(name: string): void {
    const n = this.normalizeName(name);
    if (!n) return;

    if (this.selectedNames.has(n)) this.selectedNames.delete(n);
    else this.selectedNames.add(n);

    this.recomputeSelectAll();
    this.cdr.markForCheck();
  }

  private recomputeSelectAll(): void {
    this.selectAll = this.sims.length > 0 && this.selectedNames.size === this.sims.length;
  }

  private syncSimsFromStore(): void {
    const runs = this.store.getRunsSnapshot();
    const baselineRun = runs.find((r) => (r.title || '').trim().toLowerCase() === 'basic simulation') ?? null;

    const baselineParams: T0SimulationParameters = {
      '% of reduction': 0,
      '% of increase treatment': 0,
      '% of increase diagnosis': 0
    } as T0SimulationParameters;

    const others = runs
      .filter((r) => r !== baselineRun)
      .map((r) => ({
        name: r.title,
        createdAt: r.createdAt,
        params: this.extractParamsFromRun(r) ?? this.buildParams()
      }));

    const baselineUi: UiSim[] = baselineRun
      ? [{ name: 'Baseline', createdAt: baselineRun.createdAt, params: baselineParams }]
      : [{ name: 'Baseline', createdAt: Date.now(), params: baselineParams }];

    this.sims = [...baselineUi, ...others];
  }

  private extractParamsFromRun(run: any): T0SimulationParameters | null {
    const ui = run?.t0?.__ui_params;
    if (ui) {
      return {
        '% of reduction': Number(ui['% of reduction'] ?? 0),
        '% of increase treatment': Number(ui['% of increase treatment'] ?? 0),
        '% of increase diagnosis': Number(ui['% of increase diagnosis'] ?? 0)
      } as T0SimulationParameters;
    }

    return {
      '% of reduction': 0,
      '% of increase treatment': 0,
      '% of increase diagnosis': 0
    } as T0SimulationParameters;
  }

  private normalizeName(s: string): string {
    return (s || '').trim();
  }

  private isBaselineName(name: string): boolean {
    return (name || '').trim().toLowerCase() === 'baseline';
  }

  isBaselineUi(sim: UiSim): boolean {
    return this.isBaselineName(sim.name);
  }

  private hasName(name: string): boolean {
    const n = name.trim().toLowerCase();
    return this.sims.some((s) => (s.name || '').trim().toLowerCase() === n);
  }

  private nextDefaultName(): string {
    const usedNums = new Set<number>();
    for (const s of this.sims) {
      const m = /^simulation\s+(\d+)$/i.exec((s.name || '').trim());
      if (m) usedNums.add(Number(m[1]));
    }
    let i = 1;
    while (usedNums.has(i)) i++;
    return `Simulation ${i}`;
  }

  private isOkStatus(res: any, allowed: string[]): boolean {
    const s = String(res?.status || '').trim().toUpperCase();
    return allowed.includes(s);
  }

  private sameParams(a: T0SimulationParameters, b: T0SimulationParameters): boolean {
    return (
      Number((a as any)['% of reduction']) === Number((b as any)['% of reduction']) &&
      Number((a as any)['% of increase treatment']) === Number((b as any)['% of increase treatment']) &&
      Number((a as any)['% of increase diagnosis']) === Number((b as any)['% of increase diagnosis'])
    );
  }

  private currentBaselineParams(): T0SimulationParameters {
    return {
      '% of reduction': 0,
      '% of increase treatment': 0,
      '% of increase diagnosis': 0
    } as T0SimulationParameters;
  }

  private isUnchangedFromBaseline(params: T0SimulationParameters): boolean {
    return this.sameParams(params, this.currentBaselineParams());
  }

  private hasDuplicateParams(params: T0SimulationParameters): boolean {
    return this.sims.some((s) => !this.isBaselineName(s.name) && this.sameParams(s.params, params));
  }

  private buildParams(): T0SimulationParameters {
    return {
      '% of reduction': Number(this.preventionKnob || 0),
      '% of increase treatment': Number(this.treatmentKnob || 0),
      '% of increase diagnosis': Number(this.screeningKnob || 0)
    } as T0SimulationParameters;
  }

  private fmt(n: number): string {
    return new Intl.NumberFormat('en-US').format(Math.round(n));
  }

  private fmtCompact(n: number): string {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(Math.round(n));
  }

  get preventionValueDisplay(): string {
    return this.toDisplay2(this.preventionValue);
  }

  get screeningValueDisplay(): string {
    return this.toDisplay2(this.screeningValue);
  }

  get treatmentValueDisplay(): string {
    return this.toDisplay2(this.treatmentValue);
  }

  get preventionBaseDisplay(): string {
    return this.toDisplay2(this.preventionBase);
  }

  get screeningBaseDisplay(): string {
    return this.toDisplay2(this.screeningBase);
  }

  get treatmentBaseDisplay(): string {
    return this.toDisplay2(this.treatmentBase);
  }

  private toDisplay2(n: number): string {
    const v = Number(n);
    return Number.isFinite(v) ? v.toFixed(2) : '0.00';
  }
}